/**
 * Invalidate schema-driven async-select dropdown caches (RhfAsyncSelect / useQuery).
 *
 * Keys: `['async-select-fn', 'FETCH_XYZ']` or with params
 * `['async-select-fn', 'FETCH_XYZ:{"planId":"..."}']`.
 *
 * Separate from `invalidateActionCache`, which only clears globalCache for
 * lifecycle form fetchers — not mounted dropdown option queries.
 */

/** Minimal QueryClient surface — avoids a react-query dependency in cache-manager. */
export interface AsyncSelectQueryClient {
  invalidateQueries(
    filters: {
      predicate: (query: { queryKey: unknown }) => boolean;
    },
  ): Promise<unknown> | unknown;
  refetchQueries?(
    filters: {
      predicate: (query: { queryKey: unknown }) => boolean;
      active?: boolean;
    },
  ): Promise<unknown> | unknown;
}

const ASYNC_SELECT_FN_PREFIX = 'async-select-fn';

function matchesAsyncSelectActionId(queryKey: unknown, actionId: string): boolean {
  if (!Array.isArray(queryKey) || queryKey[0] !== ASYNC_SELECT_FN_PREFIX) return false;
  const fnKey = queryKey[1];
  if (typeof fnKey !== 'string') return false;
  return fnKey === actionId || fnKey.startsWith(`${actionId}:`);
}

export function matchesAsyncSelectQueryKey(queryKey: unknown, actionId: string): boolean {
  return matchesAsyncSelectActionId(queryKey, actionId);
}

/**
 * Invalidate dropdown options for the given ActionRegistry FETCH_* action ids.
 * Mounted RhfAsyncSelect fields refetch on next render/menu open.
 */
export function invalidateAsyncSelectOptions(
  queryClient: AsyncSelectQueryClient,
  actionIds: string[],
): void {
  const ids = actionIds.map((id) => id?.trim()).filter(Boolean);
  if (ids.length === 0) return;

  const predicate = (query: { queryKey: unknown }) =>
    ids.some((actionId) => matchesAsyncSelectActionId(query.queryKey, actionId));

  void queryClient.invalidateQueries({ predicate });
  void queryClient.refetchQueries?.({ predicate, active: true });
}
